import React from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { ITeam, IPlayer } from "../../types";
import EditIcon from "@material-ui/icons/EditOutlined";
import { IconButton } from "@material-ui/core";
import { ActionLink } from "../atoms";
import Link from "../link";
import Button from "../button";
import useIsManager from "../../hooks/useIsManager";
import { injectIntl } from "react-intl";
import messages from "./PageViewTeam.intl";

const List = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const PlayerRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.75, 0),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const NumberBadge = styled("div")(({ theme }: IStyledArguments) => ({
  width: 40,
  height: 40,
  minWidth: 40,
  borderRadius: "50%",
  background: theme.palette.extra.accentSoft,
  color: theme.palette.primary.dark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "0.95rem",
}));

const PlayerLink = styled(Link)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flex: 1,
  minWidth: 0,
}));

const PlayerInfo = styled("div")({
  flex: 1,
  minWidth: 0,
});

const PlayerName = styled("div")(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  fontSize: "0.95rem",
  color: theme.palette.text.primary,
  lineHeight: 1.3,
}));

const PlayerPosition = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.8rem",
  color: theme.palette.text.secondary,
}));

const GoalsValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1rem",
  fontVariantNumeric: "tabular-nums",
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
}));

const EmptyText = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  color: theme.palette.text.secondary,
  padding: theme.spacing(3, 0),
  fontSize: "0.95rem",
}));

interface ITeamSummaryCardProps {
  id: string;
  team: ITeam;
  currentUser: {
    id: string;
    role: string;
  };
}

const SquadCard = injectIntl<ITeamSummaryCardProps>(
  ({ team, currentUser, intl }) => {
    const isManager = currentUser && useIsManager(currentUser.role);

    return (
      <>
        <List>
          {team.squad && team.squad.length > 0 ? (
            team.squad.map((player: IPlayer) => (
              <PlayerRow key={player.id}>
                <PlayerLink to={`/players/${player.id}/information`}>
                  <NumberBadge>{player.number || "-"}</NumberBadge>
                  <PlayerInfo>
                    <PlayerName>
                      {player.name} {player.surname}
                    </PlayerName>
                    {player.position && (
                      <PlayerPosition>{player.position}</PlayerPosition>
                    )}
                  </PlayerInfo>
                </PlayerLink>
                <GoalsValue>
                  {player.totalGoals || 0} {intl.formatMessage(messages.goals).toLowerCase()}
                </GoalsValue>
                {isManager && (
                  <Link to={`/players/${player.id}/edit`}>
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Link>
                )}
              </PlayerRow>
            ))
          ) : (
            <EmptyText>{intl.formatMessage(messages.squad)}: 0</EmptyText>
          )}
        </List>

        {isManager && (
          <ActionLink to="/players/newplayer">
            <Button
              fullWidth
              color="primary"
              style={{
                marginTop: 16,
                padding: "12px 32px",
                borderRadius: "10px",
                fontWeight: 600,
              }}
            >
              Add player
            </Button>
          </ActionLink>
        )}
      </>
    );
  }
);

export default SquadCard;
